import SearchPrediction, {
  createSearchPredictionModel,
} from 'data/search-predictions';
import googleQuery from './api/placePredictions';
import { AutocompletePredictionPredictionCategory, QueryResultType, QuerySettingsLimitType, QuerySettingsType, SearchPredictionSource } from './types';
import { getPredictionCategory } from './predictionClassifier';
import { CountryCode, CountryCodeList } from 'types/countries';

export const US_AND_CANADA_COUNTRY_FILTER: readonly CountryCode[] = [CountryCodeList.CANADA, CountryCodeList.UNITED_STATES];
export const CANADA_COUNTRY_FILTER: readonly CountryCode[] = [CountryCodeList.CANADA];

export const DefaultQuerySettings: QuerySettingsType = {
  limits: {
    city: 2, neighborhood: 2, street: 1, province: 1,
  },
};

/**
 * Fetches location predictions from Google Maps Places API.
 *
 * Returns cities, neighborhoods, and streets but excludes provinces/states since we get those
 * from go-search service.
 *
 * @param input - The search query string
 * @param useUsListings - Whether to include US listings (affects country filtering)
 * @param country - The country code to search within
 * @param settings - Query settings including limits for each prediction type
 * @returns Promise containing array of search predictions from Google Maps
 */
export async function getGooglePredictions(
  input: string,
  useUsListings: boolean,
  country: CountryCode,
  settings: QuerySettingsType = DefaultQuerySettings
): Promise<SearchPrediction[]> {
  const countryFilter: readonly CountryCode[] = useUsListings ? US_AND_CANADA_COUNTRY_FILTER : [country];

  // Fetch predictions with limits applied directly from Google API
  const googlePredictions = await googleQuery(input, countryFilter);

  // Convert all grouped predictions to SearchPrediction format
  const allPredictions = [
    ...autocompletePredictionToSearchResults(googlePredictions.city),
    ...autocompletePredictionToSearchResults(googlePredictions.neighborhood),
    ...autocompletePredictionToSearchResults(googlePredictions.street),
  ];

  const searchPredictions = allPredictions.map(prediction => createSearchPredictionModel(prediction as SearchPrediction));

  return searchPredictions;
}

export function autocompletePredictionToSearchResults(autocompletePrediction: google.maps.places.AutocompletePrediction[]) {
  return autocompletePrediction.map(prediction => {
    let label;
    const predictionCategory = getPredictionCategory(prediction?.types);
    switch (predictionCategory) {
    case AutocompletePredictionPredictionCategory.CITY:
      label = 'city';
      break;
    case AutocompletePredictionPredictionCategory.NEIGHBORHOOD:
      label = 'neighbourhood';
      break;
    case AutocompletePredictionPredictionCategory.STREET:
      label = 'street';
      break;
    default:
      label = 'city';
    }

    return {
      id: prediction.place_id,
      description: prediction.description.replace(', Canada', '').replace(', USA', ''),
      group: 'locations',
      matchedSubstrings: prediction.matched_substrings,
      data: null,
      label,
      source: SearchPredictionSource.GOOGLE,
    };
  });
}
